import { Alert, Button, Card, DatePicker, Form, Input, Popconfirm, Space, message } from 'antd';
import { useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import LmsStatusTag from '@/pages/education/components/LmsStatusTag';
import LmsUnitSelect from '@/pages/education/components/LmsUnitSelect';
import { EducationSectionSelect, EducationSubjectSelect } from '@/pages/education/components/EducationLookupSelects';
import { lmsErrorMessage, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Plan = {
  id: string; section_id: string; subject_id: string; curriculum_unit_id?: string | null;
  title: string; planned_on: string; status: string; body_text?: string | null;
};
type Values = {
  section_id: string; subject_id: string; curriculum_unit_id?: string;
  title: string; planned_on: Dayjs; body_text?: string;
};

const LIST_PATH = '/education/lms/lesson-plans';

export default function LessonPlanFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canManage } = useLmsAccess();
  const [form] = Form.useForm<Values>();
  const subjectId = Form.useWatch('subject_id', form);

  const detail = useQuery({
    queryKey: ['education-lms-lesson-plan', id],
    enabled: !isNew,
    queryFn: async () => (await api.get<Plan>(`/education/lms/lesson-plans/${id}`)).data,
  });
  const plan = detail.data;
  useEffect(() => {
    if (!plan) return;
    form.setFieldsValue({
      section_id: plan.section_id,
      subject_id: plan.subject_id,
      curriculum_unit_id: plan.curriculum_unit_id || undefined,
      title: plan.title,
      planned_on: dayjs(plan.planned_on),
      body_text: plan.body_text || undefined,
    });
  }, [plan, form]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-lms-lesson-plans'] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-lesson-plan', id] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-summary'] });
  };
  const save = useMutation({
    mutationFn: (values: Values) => {
      const payload = {
        ...values,
        planned_on: values.planned_on.format('YYYY-MM-DD'),
        curriculum_unit_id: values.curriculum_unit_id || null,
        body_text: values.body_text?.trim() || null,
      };
      return isNew
        ? api.post<Plan>('/education/lms/lesson-plans', payload)
        : api.put<Plan>(`/education/lms/lesson-plans/${id}`, {
          title: payload.title, planned_on: payload.planned_on,
          curriculum_unit_id: payload.curriculum_unit_id, body_text: payload.body_text,
        });
    },
    onSuccess: () => { message.success(t('lmsSaved', { defaultValue: 'Saved' })); refresh(); navigate(LIST_PATH); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to save lesson plan')),
  });
  const publish = useMutation({
    mutationFn: () => api.post(`/education/lms/lesson-plans/${id}/publish`),
    onSuccess: () => { message.success(t('lmsPublishedOk', { defaultValue: 'Published' })); refresh(); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to publish lesson plan')),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/education/lms/lesson-plans/${id}`),
    onSuccess: () => { refresh(); navigate(LIST_PATH); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to delete lesson plan')),
  });

  return (
    <EducationPageChrome
      title={isNew ? t('lmsNewLessonPlan', { defaultValue: 'New lesson plan' }) : t('lmsEditLessonPlan', { defaultValue: 'Edit lesson plan' })}
      tags={plan ? <LmsStatusTag status={plan.status} /> : undefined}
      extra={<Button onClick={() => navigate(LIST_PATH)}>{t('lmsLessonPlans', { defaultValue: 'Lesson plans' })}</Button>}
    >
      {!canManage ? <Alert type="warning" showIcon message={t('lmsReadOnly', { defaultValue: 'Read-only access.' })} /> : null}
      <Card size="small" style={{ maxWidth: 820 }} loading={!isNew && detail.isLoading}>
        <Form<Values> form={form} layout="vertical" disabled={!canManage} onFinish={(v) => save.mutate(v)}>
          <Form.Item name="section_id" label={t('section', { defaultValue: 'Section' })} rules={[{ required: true }]}>
            <EducationSectionSelect disabled={!isNew} />
          </Form.Item>
          <Form.Item name="subject_id" label={t('subject', { defaultValue: 'Subject' })} rules={[{ required: true }]}>
            <EducationSubjectSelect disabled={!isNew} onChange={() => form.setFieldValue('curriculum_unit_id', undefined)} />
          </Form.Item>
          <Form.Item name="curriculum_unit_id" label={t('lmsCurriculumUnit', { defaultValue: 'Curriculum unit (optional)' })}>
            <LmsUnitSelect subjectId={subjectId} />
          </Form.Item>
          <Form.Item name="title" label={t('title', { defaultValue: 'Title' })} rules={[{ required: true, max: 250 }]}>
            <Input maxLength={250} />
          </Form.Item>
          <Form.Item name="planned_on" label={t('lmsPlannedOn', { defaultValue: 'Planned on' })} rules={[{ required: true }]}>
            <DatePicker />
          </Form.Item>
          <Form.Item name="body_text" label={t('lmsBodyText', { defaultValue: 'Content' })}>
            <Input.TextArea rows={8} maxLength={20000} showCount />
          </Form.Item>
          {canManage ? (
            <Space wrap>
              <Button type="primary" htmlType="submit" loading={save.isPending}>{t('save', { defaultValue: 'Save' })}</Button>
              {!isNew && plan?.status === 'draft' ? (
                <Button onClick={() => publish.mutate()} loading={publish.isPending}>{t('lmsPublish', { defaultValue: 'Publish' })}</Button>
              ) : null}
              {!isNew ? (
                <Popconfirm title={t('lmsDelete', { defaultValue: 'Delete' })} onConfirm={() => remove.mutate()}>
                  <Button danger>{t('lmsDelete', { defaultValue: 'Delete' })}</Button>
                </Popconfirm>
              ) : null}
              <Button onClick={() => navigate(LIST_PATH)}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
            </Space>
          ) : null}
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
